import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowRight, MagnifyingGlass, Tag, Clock, Eye, BookOpen,
  CaretRight, Sparkle, Funnel
} from "@phosphor-icons/react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || "");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchData();
  }, [selectedCategory, currentPage]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [postsRes, categoriesRes, tagsRes, featuredRes] = await Promise.all([
        axios.get(`${API}/blog/posts`, {
          params: {
            category: selectedCategory || undefined,
            page: currentPage,
            limit: 9
          }
        }),
        axios.get(`${API}/blog/categories`),
        axios.get(`${API}/blog/tags`),
        axios.get(`${API}/blog/featured`)
      ]);

      setPosts(postsRes.data.posts || []);
      setTotalPages(postsRes.data.pages || 1);
      setCategories(categoriesRes.data.categories || []);
      setTags(tagsRes.data.tags || []);
      setFeatured(featuredRes.data.featured || []);
    } catch (error) {
      console.error("Error fetching blog data:", error);
      // Seed blog posts if none exist
      try {
        await axios.post(`${API}/blog/seed`);
        // Retry fetching
        const postsRes = await axios.get(`${API}/blog/posts`);
        setPosts(postsRes.data.posts || []);
      } catch (seedError) {
        console.error("Error seeding blog:", seedError);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    
    try {
      const res = await axios.get(`${API}/blog/search`, {
        params: { q: searchQuery }
      });
      setPosts(res.data.results || []);
    } catch (error) {
      console.error("Search error:", error);
    }
  };

  const handleCategoryClick = (categoryId) => {
    setSelectedCategory(categoryId === selectedCategory ? "" : categoryId);
    setCurrentPage(1);
    setSearchParams(categoryId ? { category: categoryId } : {});
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div data-testid="blog-page" className="overflow-hidden">
      {/* Hero Section */}
      <section className="section-padding bg-slate-50 relative overflow-hidden" data-testid="blog-hero">
        <div className="absolute inset-0 bg-mesh-gradient" />
        <div className="absolute inset-0 decorative-grid opacity-30" />
        
        <motion.div 
          className="absolute top-20 right-1/4 w-96 h-96 rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(6, 182, 212, 0.15) 0%, transparent 70%)' }}
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        
        <div className="max-w-7xl mx-auto container-padding relative">
          <motion.div
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <span className="badge badge-accent mb-8">
              <BookOpen weight="fill" className="w-4 h-4 mr-2" />
              Insights & Resources
            </span>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-slate-900 mb-8 leading-[1.05] tracking-tight">
              Hospice Leadership{" "}
              <span className="text-gradient">Insights</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed mb-10">
              Expert guidance on compliance, operations, and technology 
              for hospice and home health leaders.
            </p>
            
            {/* Search Bar */}
            <form onSubmit={handleSearch} className="max-w-xl mx-auto">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full h-14 pl-14 pr-6 bg-white border-2 border-slate-200 rounded-full focus:outline-none focus:border-cyan-500 transition-all text-slate-900"
                  data-testid="blog-search"
                />
                <MagnifyingGlass className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <button 
                  type="submit"
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-6 py-2 rounded-full font-medium hover:shadow-lg transition-all"
                >
                  Search
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </section>

      {/* Categories Filter */}
      <section className="py-8 bg-white border-b border-slate-100" data-testid="blog-categories">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="flex items-center gap-4 overflow-x-auto pb-2">
            <span className="text-slate-500 font-medium flex items-center gap-2 flex-shrink-0">
              <Funnel weight="fill" className="w-4 h-4" />
              Filter:
            </span>
            <button
              onClick={() => handleCategoryClick("")}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex-shrink-0 ${
                !selectedCategory 
                  ? "bg-gradient-to-r from-cyan-500 to-blue-500 text-white" 
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
              data-testid="category-all"
            >
              All Posts
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => handleCategoryClick(cat.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex-shrink-0 ${
                  selectedCategory === cat.id 
                    ? "bg-gradient-to-r from-cyan-500 to-blue-500 text-white" 
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
                data-testid={`category-${cat.id}`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-padding bg-white" data-testid="blog-posts">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
            {/* Posts Grid */}
            <div className="lg:col-span-3">
              {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div key={i} className="bg-slate-100 rounded-2xl h-80 animate-pulse" />
                  ))}
                </div>
              ) : posts.length === 0 ? (
                <div className="text-center py-20">
                  <BookOpen weight="duotone" className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="font-display text-xl text-slate-900 mb-2">No posts found</h3>
                  <p className="text-slate-600">Check back soon for new content!</p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {posts.map((post, index) => (
                      <motion.article
                        key={post.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="bg-white border border-slate-100 rounded-2xl overflow-hidden hover:shadow-xl hover:border-cyan-500/30 transition-all duration-300 group"
                        data-testid={`blog-post-${post.slug}`}
                      >
                        <Link to={`/blog/${post.slug}`}>
                          {post.featured_image && (
                            <div className="aspect-video overflow-hidden">
                              <img
                                src={post.featured_image}
                                alt={post.title}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                              />
                            </div>
                          )}
                          <div className="p-6">
                            <div className="flex items-center gap-3 mb-3">
                              <span className="text-xs font-medium text-cyan-600 bg-cyan-50 px-3 py-1 rounded-full">
                                {categories.find(c => c.id === post.category)?.name || post.category}
                              </span>
                              <span className="text-xs text-slate-400 flex items-center gap-1">
                                <Eye weight="fill" className="w-3 h-3" />
                                {post.views}
                              </span>
                            </div>
                            <h2 className="font-display font-bold text-lg text-slate-900 mb-3 group-hover:text-cyan-600 transition-colors line-clamp-2">
                              {post.title}
                            </h2>
                            <p className="text-slate-600 text-sm line-clamp-3 mb-4">
                              {post.excerpt}
                            </p>
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-slate-400 flex items-center gap-1">
                                <Clock weight="fill" className="w-3 h-3" />
                                {formatDate(post.published_at)}
                              </span>
                              <span className="text-cyan-600 font-medium text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                                Read More
                                <CaretRight weight="bold" className="w-4 h-4" />
                              </span>
                            </div>
                          </div>
                        </Link>
                      </motion.article>
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex justify-center gap-2 mt-12">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`w-10 h-10 rounded-full font-medium transition-all ${
                            currentPage === page
                              ? "bg-gradient-to-r from-cyan-500 to-blue-500 text-white"
                              : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                          }`}
                        >
                          {page}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1 space-y-8">
              {/* Featured Posts */}
              {featured.length > 0 && (
                <div className="bg-slate-50 rounded-2xl p-6">
                  <h3 className="font-display font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <Sparkle weight="fill" className="w-5 h-5 text-cyan-500" />
                    Popular Articles
                  </h3>
                  <div className="space-y-4">
                    {featured.slice(0, 5).map((post) => (
                      <Link
                        key={post.id}
                        to={`/blog/${post.slug}`}
                        className="block group"
                        data-testid={`featured-${post.slug}`}
                      >
                        <h4 className="text-sm font-medium text-slate-700 group-hover:text-cyan-600 transition-colors line-clamp-2">
                          {post.title}
                        </h4>
                        <span className="text-xs text-slate-400">{post.views} views</span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Tags */}
              {tags.length > 0 && (
                <div className="bg-slate-50 rounded-2xl p-6">
                  <h3 className="font-display font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <Tag weight="fill" className="w-5 h-5 text-cyan-500" />
                    Topics
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {tags.slice(0, 15).map((tag) => (
                      <span
                        key={tag.name}
                        className="text-xs bg-white border border-slate-200 text-slate-600 px-3 py-1.5 rounded-full hover:border-cyan-500 hover:text-cyan-600 cursor-pointer transition-colors"
                      >
                        {tag.name} ({tag.count})
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* CTA */}
              <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-6 text-white">
                <h3 className="font-display font-bold text-lg mb-3">
                  Need Expert Guidance?
                </h3>
                <p className="text-slate-400 text-sm mb-4">
                  Schedule a free consultation with our hospice compliance experts.
                </p>
                <Link
                  to="/contact"
                  className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-medium px-5 py-2.5 rounded-full text-sm inline-flex items-center gap-2 hover:shadow-lg transition-all"
                >
                  Get Started
                  <ArrowRight weight="bold" className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
